import { itemEquipmentIds, itemMaterials } from '../../domain/entities/schedule.ts';
import type { Equipment } from '../../domain/entities/equipment.ts';
import type { EquipmentBooking } from '../../domain/entities/equipment-booking.ts';
import { pickPlant, type PickedPlant } from './pick-plant.ts';
import { z } from 'zod';
import { minDateOnly, type DateOnly } from '../../domain/date-only.ts';
import { INTERNAL_ONLY } from '../../domain/entities/common.ts';
import { newId } from '../../domain/id.ts';
import type {
  ScheduleItem,
  SchedulePhase,
  TemplateItem,
  TemplatePhase,
} from '../../domain/entities/index.ts';
import { ScheduleTemplateSchema, type ScheduleTemplate } from '../../domain/entities/index.ts';
import {
  addWorkingDays,
  endDateFor,
  nextWorkingDay,
  workingDaysBetween,
  type WorkCalendar,
} from '../../domain/work-calendar.ts';
import { createWrite, type BatchWrite } from '../../ports/batch.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { NotFoundError } from '../../ports/repository.ts';
import { listScheduleItems } from './save-schedule-item.ts';
import { listSchedulePhases } from './save-schedule-phase.ts';
import type { SchedulePlanContext } from './schedule-plan.ts';

export class TemplateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TemplateError';
  }
}

export const SaveTemplateInputSchema = z.object({
  name: z.string().trim().min(1, 'Give the template a name').max(200),
  description: z.string().trim().max(1000).optional(),
});
export type SaveTemplateInput = z.input<typeof SaveTemplateInputSchema>;

function templates(context: { data: DataPort; companyId: string }) {
  return context.data.collection<ScheduleTemplate>(
    { companyId: context.companyId },
    'scheduleTemplates',
  );
}

export async function listScheduleTemplates(context: {
  data: DataPort;
  companyId: string;
}): Promise<ScheduleTemplate[]> {
  const page = await templates(context).list({
    orderBy: [{ field: 'name', direction: 'asc' }],
  });
  return [...page.items];
}

/**
 * Turn a job's schedule into a reusable template.
 *
 * Offsets are measured from the earliest item, in working days, so applying
 * the result anywhere reproduces the same spacing of work. Dates themselves
 * are not kept: a template that remembered September would be wrong every
 * other month of the year.
 *
 * Only structure travels. Assignees, confirmations, progress, baselines and
 * shift history all belong to the job they happened on, and carrying them
 * into a new job would be stating as fact things nobody has done yet.
 */
export async function saveScheduleAsTemplate(
  context: SchedulePlanContext,
  calendar: WorkCalendar,
  input: SaveTemplateInput,
): Promise<ScheduleTemplate> {
  const parsed = SaveTemplateInputSchema.safeParse(input);
  if (!parsed.success) throw new TemplateError(parsed.error.issues[0]!.message);

  const [items, phases] = await Promise.all([
    listScheduleItems(context),
    listSchedulePhases(context),
  ]);
  if (items.length === 0) {
    throw new TemplateError('There is nothing on this schedule to save as a template');
  }

  // Computed rather than taken from the first item: `listScheduleItems`
  // happens to order by start date, but a template anchored on the wrong day
  // would skew every offset in it, and that is too quiet a failure to rest on
  // somebody else's ORDER BY.
  const anchor = items.reduce<DateOnly>(
    (earliest, item) => minDateOnly(earliest, item.startDate),
    items[0]!.startDate,
  );

  const phaseIndexById = new Map(phases.map((phase, index) => [phase.id, index]));
  const itemIndexById = new Map(items.map((item, index) => [item.id, index]));
  const now = context.clock.now().toISOString();

  const template: ScheduleTemplate = {
    id: newId(),
    companyId: context.companyId,
    name: parsed.data.name,
    description: parsed.data.description ? parsed.data.description : undefined,
    phases: phases.map((phase) => ({ name: phase.name, colour: phase.colour })),
    items: items.map((item) => ({
      title: item.title,
      colour: item.colour,
      phaseIndex: item.phaseId ? phaseIndexById.get(item.phaseId) : undefined,
      // Inclusive counting elsewhere means the anchor itself is day one, so
      // an offset of zero is the item that starts the job.
      startOffsetWorkDays: workingDaysBetween(calendar, anchor, item.startDate) - 1,
      workDays: item.workDays,
      /**
       * The *types* of what this item books, not the units.
       *
       * Resolved by the caller, which has the register in hand: a
       * template naming `EX-04` would book the wrong machine on the next
       * job, or none at all on another company's. An item whose plant
       * cannot be resolved to a type carries nothing rather than
       * guessing.
       */
      equipmentTypeIds: itemEquipmentIds(item).flatMap((equipmentId) => {
        const typeId = context.equipmentTypeOf?.(equipmentId);
        return typeId ? [typeId] : [];
      }),
      // Carried as ids and quantities, never prices: the catalog's prices
      // move, and a template that froze one would estimate next year's
      // job at last year's cost.
      materials: itemMaterials(item).map((material) => ({ ...material })),
      reminder: item.reminder,
      predecessors: item.predecessors.flatMap((predecessor) => {
        const itemIndex = itemIndexById.get(predecessor.itemId);
        // A predecessor pointing outside this schedule cannot be expressed
        // relatively, so it is dropped rather than guessed at.
        return itemIndex === undefined
          ? []
          : [{ itemIndex, type: predecessor.type, lagDays: predecessor.lagDays }];
      }),
    })),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  // Validated before it is stored: a template is applied many times, so a
  // malformed one is a bug repeated on every future job.
  const checked = ScheduleTemplateSchema.safeParse(template);
  if (!checked.success) throw new TemplateError(checked.error.issues[0]!.message);

  await templates(context).create(template);
  return template;
}

export interface AppliedTemplate {
  readonly phases: readonly SchedulePhase[];
  readonly items: readonly ScheduleItem[];
  /**
   * What the apply would book, and what it could not (SCRUM-210).
   *
   * Returned rather than only applied, because the caller shows this
   * before anything is written — "this adds 34 items and books 6
   * machines; 2 are already wanted elsewhere" is the difference between a
   * tool and a trap, which is the same reason `expandTemplate` exists
   * apart from the write at all.
   */
  readonly plant?: PickedPlant;
}

/** The register and the diary, for turning template types into bookings. */
export interface TemplatePlant {
  readonly fleet: readonly Equipment[];
  readonly existing: readonly EquipmentBooking[];
  readonly jobName: string;
}

/**
 * Build the phases and items a template produces, without writing them.
 *
 * Separated from the write so the caller can show what is about to be added
 * — applying a template to a job that already has a schedule is additive,
 * and seeing "this adds 34 items" first is the difference between a tool and
 * a trap.
 */
export function expandTemplate(
  calendar: WorkCalendar,
  template: ScheduleTemplate,
  target: { companyId: string; jobId: string; uid: string; startDate: DateOnly; at: string },
  /**
   * Omit to expand without booking anything.
   *
   * Optional so a caller that only wants the shape of a template — a
   * preview, a test, a company with no register — gets it without having
   * to produce a fleet first.
   */
  plant?: TemplatePlant,
): AppliedTemplate {
  const audit = {
    createdAt: target.at,
    createdBy: target.uid,
    updatedAt: target.at,
    updatedBy: target.uid,
  };
  const start = nextWorkingDay(calendar, target.startDate);

  const phases: SchedulePhase[] = template.phases.map((phase: TemplatePhase, index) => ({
    id: newId(),
    companyId: target.companyId,
    jobId: target.jobId,
    name: phase.name,
    colour: phase.colour,
    order: index,
    ...audit,
  }));

  // Ids first, so a predecessor can point at an item built later in the loop.
  const itemIds = template.items.map(() => newId());

  const items: ScheduleItem[] = template.items.map((item: TemplateItem, index) => {
    const startDate =
      item.startOffsetWorkDays === 0
        ? start
        : addWorkingDays(calendar, start, item.startOffsetWorkDays);
    return {
      id: itemIds[index]!,
      companyId: target.companyId,
      jobId: target.jobId,
      title: item.title,
      colour: item.colour,
      phaseId: item.phaseIndex === undefined ? undefined : phases[item.phaseIndex]?.id,
      tagIds: [],
      assignees: [],
      // Filled in below, once every item's dates are known: picking a
      // machine needs the whole template in view, or two activities that
      // overlap would both be handed the same one.
      equipmentIds: [],
      // `?? []` because a template saved before materials existed has no
      // such field, and applying one would otherwise throw. The same
      // hazard as `itemMaterials` on a schedule item, one type along.
      materials: (item.materials ?? []).map((material) => ({ ...material })),
      startDate,
      workDays: item.workDays,
      endDate: endDateFor(calendar, startDate, item.workDays),
      progress: 0,
      completed: false,
      reminder: item.reminder,
      predecessors: item.predecessors.flatMap((predecessor) => {
        const itemId = itemIds[predecessor.itemIndex];
        return itemId === undefined
          ? []
          : [{ itemId, type: predecessor.type, lagDays: predecessor.lagDays }];
      }),
      confirmations: {},
      shifts: [],
      notes: {},
      // Internal until somebody decides otherwise, like any new item: a
      // template should not publish work to an owner by itself.
      visibility: INTERNAL_ONLY,
      ...audit,
    };
  });

  if (!plant) return { phases, items };

  /**
   * Picked after the loop, with every item's dates in hand.
   *
   * Doing it inside the loop would check each choice only against what
   * was already in the database, so two activities in the same template
   * that overlap would both be given the same machine — and the clash
   * would appear the moment the template was applied, caused by the
   * apply itself.
   */
  const picked = pickPlant(
    {
      fleet: plant.fleet,
      existing: plant.existing,
      companyId: target.companyId,
      jobId: target.jobId,
      jobName: plant.jobName,
    },
    // `items` is built by mapping `template.items`, so the two are
    // parallel by construction and an index into one is valid in the
    // other. Written without a fallback deliberately: a `?? []` here
    // would be a branch that can never be taken, which is dead code
    // wearing the costume of safety.
    items.map((item, index) => ({
      scheduleItemId: item.id,
      title: item.title,
      startDate: item.startDate,
      endDate: item.endDate,
      equipmentTypeIds: template.items[index]!.equipmentTypeIds ?? [],
    })),
  );

  const booked: ScheduleItem[] = items.map((item) => ({
    ...item,
    // Likewise: `pickPlant` returns one entry per request, and the
    // requests were built from these same items.
    equipmentIds: picked.picks
      .get(item.id)!
      .flatMap((pick) => (pick.equipmentId ? [pick.equipmentId] : [])),
  }));

  return { phases, items: booked, plant: picked };
}

/**
 * Add a template's work to a job, in one batch.
 *
 * All or nothing, because half a template is worse than none: the items that
 * landed would carry predecessors pointing at items that did not, and the
 * person applying it would have to unpick a partial skeleton by hand.
 *
 * Additive rather than replacing. Wiping an existing schedule because
 * somebody picked the wrong template from a list is not a recoverable
 * mistake, and nothing in the spec asks for it.
 */
/**
 * The fleet and the existing bookings, or nothing.
 *
 * Returns `undefined` rather than throwing when the reads are refused.
 * A project manager who cannot see the register should still be able to
 * apply a template — they simply get the work without the plant, and the
 * caller can say so. Refusing the whole apply would make the equipment
 * module a prerequisite for scheduling.
 */
async function readPlant(
  context: SchedulePlanContext,
  template: ScheduleTemplate,
): Promise<TemplatePlant | undefined> {
  const needsPlant = template.items.some((item) => item.equipmentTypeIds.length > 0);
  if (!needsPlant) return undefined;

  const scope = { companyId: context.companyId };
  try {
    const [fleet, existing, job] = await Promise.all([
      context.data.collection<Equipment>(scope, 'equipment').list(),
      context.data.collection<EquipmentBooking>(scope, 'equipmentBookings').list(),
      context.data.collection<{ id: string; name: string }>(scope, 'jobs').get(context.jobId),
    ]);
    return {
      fleet: fleet.items,
      existing: existing.items,
      jobName: job?.name ?? 'this job',
    };
  } catch {
    return undefined;
  }
}

export async function applyTemplate(
  context: SchedulePlanContext,
  calendar: WorkCalendar,
  templateId: string,
  startDate: DateOnly,
): Promise<AppliedTemplate> {
  const template = await templates(context).get(templateId);
  if (!template) throw new NotFoundError('scheduleTemplates', templateId);

  const at = context.clock.now().toISOString();

  /**
   * The register and the diary, read here rather than inside the expander.
   *
   * `expandTemplate` stays pure so a caller can show what a template will
   * do before it does it. Both reads are refused for a role without
   * `equipment: view`, which is not an error: that person applies a
   * template that books nothing, which is the honest outcome rather than
   * a refusal to apply it at all.
   */
  const plant = await readPlant(context, template);

  const expanded = expandTemplate(
    calendar,
    template,
    {
      companyId: context.companyId,
      jobId: context.jobId,
      uid: context.uid,
      startDate,
      at,
    },
    plant,
  );

  const scope = { companyId: context.companyId, jobId: context.jobId };
  const writes: BatchWrite[] = [
    ...expanded.phases.map((phase) => createWrite(scope, 'schedulePhases', phase)),
    ...expanded.items.map((item) => createWrite(scope, 'scheduleItems', item)),
  ];

  if (writes.length > 0) await context.data.commit(writes);
  return expanded;
}

export async function deleteScheduleTemplate(
  context: { data: DataPort; companyId: string },
  templateId: string,
): Promise<void> {
  const repo = templates(context);
  if (!(await repo.get(templateId))) throw new NotFoundError('scheduleTemplates', templateId);
  await repo.delete(templateId);
}
